import { NavLink, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  BarChart3,
  Wallet,
  ShoppingCart,
  Undo2,
  Receipt,
  Package,
  FileStack,
  Building2,
  Warehouse,
  CreditCard,
  Store,
  LogOut,
  Truck,
  MessagesSquare,
  MapPinned,
  Users,
  Trophy,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

import { useAuthStore } from '@/store/auth.store';
import { useChatStore } from '@/features/chat/store';
import logoMark from '@/images/optoviklogo.png';

export interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
}

export const NAV_GROUPS: Array<{ title?: string; items: NavItem[] }> = [
  {
    items: [
      { to: '/', label: 'Главная', icon: LayoutDashboard },
      { to: '/analytics', label: 'Аналитика', icon: BarChart3 },
      { to: '/finance', label: 'Финансы', icon: Wallet },
      { to: '/expenses', label: 'Расходы', icon: Receipt },
    ],
  },
  {
    title: 'Торговля',
    items: [
      { to: '/sales', label: 'Продажи', icon: ShoppingCart },
      { to: '/returns', label: 'Возвраты', icon: Undo2 },
      { to: '/couriers', label: 'Курьеры', icon: Truck },
      { to: '/stores', label: 'Магазины', icon: MapPinned },
      { to: '/ratings', label: 'Рейтинг', icon: Trophy },
    ],
  },
  {
    title: 'Склад и закупки',
    items: [
      { to: '/items', label: 'Товары', icon: Package },
      { to: '/stock', label: 'Склад', icon: Warehouse },
      { to: '/documents', label: 'Документы', icon: FileStack },
      { to: '/suppliers', label: 'Поставщики', icon: Building2 },
    ],
  },
  {
    title: 'Команда',
    items: [
      { to: '/staff', label: 'Сотрудники', icon: Users },
      { to: '/chat', label: 'Чат', icon: MessagesSquare },
    ],
  },
  {
    title: 'Аккаунт',
    items: [
      { to: '/subscription', label: 'Подписка', icon: CreditCard },
      { to: '/profile', label: 'Профиль', icon: Store },
    ],
  },
];

const statusBadge: Record<string, { label: string; cls: string }> = {
  PENDING: { label: 'На модерации', cls: 'badge-amber' },
  BLOCKED: { label: 'Заблокирован', cls: 'badge-red' },
};

export function UnreadBadge({ count, className = '' }: { count: number; className?: string }) {
  if (count <= 0) return null;
  return (
    <span
      className={`inline-flex min-w-[18px] h-[18px] items-center justify-center rounded-full bg-orange px-1 text-[11px] font-semibold leading-none text-white tabular-nums ${className}`}
    >
      {count > 99 ? '99+' : count}
    </span>
  );
}

export function Sidebar({ onNavigate }: { onNavigate?: () => void } = {}) {
  const wholesaler = useAuthStore((s) => s.wholesaler);
  const logout = useAuthStore((s) => s.logout);
  const unread = useChatStore((s) => s.unread);
  const navigate = useNavigate();

  const badge = wholesaler ? statusBadge[wholesaler.status] : undefined;

  return (
    <aside className="h-full w-full bg-rail border-r border-line flex flex-col">
      <div className="px-4 h-14 flex items-center gap-2.5 shrink-0">
        <img src={logoMark} alt="" className="w-7 h-7 rounded-md object-cover shrink-0" />
        <div className="font-semibold text-[15px] text-ink tracking-tight">Resstro Оптовик</div>
      </div>

      <div className="px-3 pb-2">
        <NavLink
          to="/profile"
          onClick={() => onNavigate?.()}
          className="flex items-center gap-2.5 rounded-lg px-2 py-2 hover:bg-ink/[0.04] transition-colors"
        >
          <div className="w-8 h-8 rounded-lg bg-white border border-line flex items-center justify-center shrink-0 overflow-hidden">
            {wholesaler?.logoUrl ? (
              <img src={wholesaler.logoUrl} alt="" className="w-full h-full object-cover" />
            ) : (
              <Store size={16} className="text-ink/50" />
            )}
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[13px] font-medium text-ink truncate">{wholesaler?.name ?? '—'}</div>
            <div className="text-xs text-ink/45 truncate">{wholesaler?.email ?? ''}</div>
          </div>
        </NavLink>
        {badge && <span className={`${badge.cls} ml-2 mt-1`}>{badge.label}</span>}
      </div>

      <nav className="flex-1 overflow-y-auto px-3 pb-3" aria-label="Разделы кабинета">
        {NAV_GROUPS.map((group, gi) => (
          <div key={gi} className={gi ? 'mt-4' : 'mt-1'}>
            {group.title && <div className="px-2 pb-1 text-xs font-medium text-ink/40">{group.title}</div>}
            <div className="space-y-px">
              {group.items.map(({ to, label, icon: Icon }) => (
                <NavLink
                  key={to}
                  to={to}
                  end={to === '/'}
                  onClick={() => onNavigate?.()}
                  className={({ isActive }) =>
                    `group flex items-center gap-2.5 h-9 px-2 rounded-lg text-sm transition-colors ${
                      isActive ? 'bg-orange-50 text-ink font-medium' : 'text-ink/70 hover:bg-ink/[0.04] hover:text-ink'
                    }`
                  }
                >
                  {({ isActive }) => (
                    <>
                      <Icon size={17} strokeWidth={1.9} className={isActive ? 'text-orange' : 'text-ink/45 group-hover:text-ink/70'} />
                      <span className="flex-1 truncate">{label}</span>
                      {to === '/chat' && <UnreadBadge count={unread} />}
                    </>
                  )}
                </NavLink>
              ))}
            </div>
          </div>
        ))}
      </nav>

      <div className="px-3 py-3 border-t border-line">
        <button
          onClick={() => {
            logout();
            navigate('/login');
          }}
          className="flex w-full items-center gap-2.5 h-9 px-2 rounded-lg text-sm text-ink/60 hover:bg-ink/[0.04] hover:text-ink transition-colors"
        >
          <LogOut size={17} strokeWidth={1.9} className="text-ink/45" />
          Выйти
        </button>
      </div>
    </aside>
  );
}
